/**
 * board-window — how much of the board's history to show.
 *
 * The Board accumulates: every piece ever published stays in its column, so
 * finding current work means scrolling past months of finished work. The
 * window trims by recency.
 *
 * **Active pieces ignore the window by default.** A piece still moving
 * through the pipeline is current work however long ago it was last touched
 * — in fact a piece stuck for three weeks is the one you most need to see,
 * and a plain date filter would hide exactly that. So the window trims
 * finished work, and the toggle exists for the rarer case of wanting a
 * strict date range.
 *
 * Pure and React-free so the rule is tested, including the stuck-piece case
 * that motivates it.
 */

/** null = all time. */
export type BoardWindow = 7 | 15 | 30 | 90 | null;

export const BOARD_WINDOWS: ReadonlyArray<{ value: BoardWindow; label: string }> = [
  { value: 7, label: "7 days" },
  { value: 15, label: "15 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: null, label: "All time" },
];

export const DEFAULT_BOARD_WINDOW: BoardWindow = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A stored value from an older build must never wedge the board, so anything
 * unrecognised falls back to the default.
 */
export function parseBoardWindow(raw: string | null | undefined): BoardWindow {
  if (raw === "all") return null;
  const n = Number(raw);
  return BOARD_WINDOWS.some((w) => w.value === n) ? (n as BoardWindow) : DEFAULT_BOARD_WINDOW;
}

export function serialiseBoardWindow(w: BoardWindow): string {
  return w === null ? "all" : String(w);
}

/**
 * Whether a timestamp falls inside the window. A missing or unparseable
 * timestamp counts as **inside**: hiding a piece because its date is
 * malformed would make it unreachable from the board with no way to tell.
 */
export function isWithinWindow(
  iso: string | null | undefined,
  days: BoardWindow,
  now: Date = new Date()
): boolean {
  if (days === null) return true;
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true;
  // A future timestamp (a scheduled publish date) is not "older than N days".
  return t >= now.getTime() - days * DAY_MS;
}

export interface BoardFilterOptions<T> {
  window: BoardWindow;
  /** Default true: keep work still in flight regardless of its age. */
  alwaysShowActive: boolean;
  /** True when the piece has not finished its run. */
  isActive: (piece: T) => boolean;
  /** The timestamp the window is measured against. */
  timestampOf: (piece: T) => string | null | undefined;
  now?: Date;
}

export interface BoardFilterResult<T> {
  visible: T[];
  /** How many the window removed, so the board can say so rather than
   *  quietly showing less than the workspace holds. */
  hidden: number;
}

export function filterBoardPieces<T>(
  pieces: readonly T[],
  opts: BoardFilterOptions<T>
): BoardFilterResult<T> {
  const now = opts.now ?? new Date();
  const visible = pieces.filter((p) => {
    if (opts.alwaysShowActive && opts.isActive(p)) return true;
    return isWithinWindow(opts.timestampOf(p), opts.window, now);
  });
  return { visible, hidden: pieces.length - visible.length };
}
